import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';

import { ResizableEditableTextInput } from '~/components/ResizableEditableTextInput';
import { type BulkEditCustomFieldModalProps } from './types';

export type BulkEditPlainTextCustomFieldModalProps = {
  onSave: (params: {
    assetIds: string[];
    boardIds: string[];
    baseCustomField: BulkEditCustomFieldModalProps['field'];
    nextPlainTextValue: string;
  }) => Promise<void>;
} & BulkEditCustomFieldModalProps;

export const BulkEditPlainTextCustomFieldModal = memo(
  ({
    field,
    assetIds,
    boardIds,
    initialValues,
    initialStatus,
    totalSelectedItemsCount,
    isLoadingInitialValues = false,
    onClose,
    onSave,
  }: AirModalProps<BulkEditPlainTextCustomFieldModalProps>) => {
    const selectedItemsCount = totalSelectedItemsCount ?? assetIds.length + boardIds.length;
    const allHaveSameValue = useMemo(() => {
      if (initialStatus === 'mixed') return false;
      const baseValue = initialValues[0]?.plainTextValue;
      return initialValues.every((cf) => cf.plainTextValue === baseValue);
    }, [initialStatus, initialValues]);

    const originalValue = allHaveSameValue ? initialValues[0]?.plainTextValue || '' : '';
    const [plainTextValue, setPlainTextValue] = useState<string>(originalValue);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTouched, setIsTouched] = useState(false);

    useEffect(() => {
      if (!isTouched) {
        setPlainTextValue(originalValue);
      }
    }, [isTouched, originalValue]);

    const onSubmit = useCallback(
      (newValue: string) => {
        if (isLoadingInitialValues) return;
        if (originalValue === newValue) {
          setIsTouched(false);
        } else {
          setPlainTextValue(newValue);
        }
      },
      [isLoadingInitialValues, originalValue],
    );

    const onChange = useCallback(() => {
      if (isLoadingInitialValues) return;
      setIsTouched(true);
    }, [isLoadingInitialValues]);

    const onConfirm = useCallback(async () => {
      setIsSubmitting(true);
      await onSave({
        assetIds,
        boardIds,
        baseCustomField: field,
        nextPlainTextValue: plainTextValue,
      });
      onClose();
    }, [assetIds, boardIds, field, onClose, onSave, plainTextValue]);

    return (
      <Modal isOpen onDismiss={onClose}>
        <div className="flex flex-col gap-6">
          <header className="flex justify-between gap-4">
            <ModalTitle>Edit {field.name}</ModalTitle>
            <ModalCloseButton className="shrink-0" onClick={onClose} />
          </header>
          <p className="text-16 text-grey-11">
            Enter a value below to apply to the {pluralize('item', selectedItemsCount, true)} you’ve selected.
          </p>
          <ResizableEditableTextInput
            label="Field value"
            placeholder="None"
            defaultValue={plainTextValue}
            onSubmit={onSubmit}
            appearance="outline"
            onChange={onChange}
            disabled={isLoadingInitialValues}
          />
          <footer className="flex justify-end gap-3">
            <Button appearance="ghost" color="grey" onClick={onClose} size="large">
              Cancel
            </Button>
            <Button
              data-testid="BULK_EDIT_PLAIN_TEXT_CUSTOM_FIELD_MODAL_SAVE"
              isLoading={isSubmitting || isLoadingInitialValues}
              disabled={!isTouched || isSubmitting || isLoadingInitialValues}
              appearance="filled"
              color="blue"
              size="large"
              onClick={onConfirm}
            >
              Save
            </Button>
          </footer>
        </div>
      </Modal>
    );
  },
);
BulkEditPlainTextCustomFieldModal.displayName = 'BulkEditPlainTextCustomFieldModal';
